import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Loader2, Sparkles, CheckCircle2 } from "lucide-react";

// Componente riutilizzabile: all'inizio di un processo carica un documento
// (documento d'identità/CF per privati, Visura per aziende), lo legge con l'AI
// (stesso sistema del flusso Energia) e passa i dati estratti al componente padre.
export function DocumentAutofill({ tipoCliente = "privato", required = false, onData, onUploadedChange, docTypesLabel }) {
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState(null);
  const inputRef = useRef(null);
  const isAzienda = tipoCliente === "azienda";
  const endpoint = isAzienda ? "/company/extract-visura" : "/company/extract-documento";

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("File troppo grande (max 6 MB)"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        const { data } = await api.post(endpoint, { file_base64: reader.result, filename: file.name });
        const d = data?.data || {};
        setFileName(file.name);
        onUploadedChange?.(true);
        onData?.(d, isAzienda ? "visura" : "documento");
        toast.success(data?.fields_found ? "Dati compilati automaticamente dal documento!" : "Documento letto: controlla i dati");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore lettura documento");
      } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 flex flex-wrap items-center gap-2" data-testid="document-autofill">
      <p className="text-sm font-medium flex items-center gap-1.5" title={isAzienda
          ? "Compila in automatico ragione sociale, P.IVA e sede leggendo la visura."
          : `Compila in automatico i dati anagrafici (${docTypesLabel || "carta d'identità, patente, tessera CF, passaporto"}).`}><Sparkles className="w-4 h-4 text-primary" />
          {isAzienda ? "Visura camerale" : "Documento del cliente"}</p>
      {required
          ? <Badge className="bg-amber-500" data-testid="document-autofill-required">Obbligatorio</Badge>
          : <Badge variant="outline" data-testid="document-autofill-optional">Opzionale</Badge>}
      <span className="text-xs text-muted-foreground hidden md:inline">compila i dati in automatico</span>
      <div className="flex items-center gap-2 flex-wrap ml-auto">
        <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onFile} data-testid="document-autofill-input" />
        <Button type="button" variant="outline" size="sm" className="gap-2" disabled={busy}
          onClick={() => inputRef.current?.click()} data-testid="document-autofill-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "Lettura…" : "Carica documento"}
        </Button>
        {fileName && <Badge variant="outline" className="gap-1" data-testid="document-autofill-file"><CheckCircle2 className="w-3 h-3 text-teal-500" /> {fileName}</Badge>}
      </div>
    </div>
  );
}

// Mappa i dati estratti dal documento d'identità sui campi anagrafica comuni.
export function mapDocToAnagrafica(d) {
  return {
    nome: d.nome || "",
    cognome: d.cognome || "",
    codice_fiscale: d.codice_fiscale || "",
    data_nascita: d.data_nascita || "",
    luogo_nascita: d.luogo_nascita || "",
  };
}

// Mappa i dati estratti dalla visura sui campi anagrafica azienda comuni.
export function mapVisuraToAnagrafica(d) {
  return {
    ragione_sociale: d.ragione_sociale || "",
    partita_iva: d.partita_iva || "",
    codice_fiscale: d.codice_fiscale || d.partita_iva || "",
    legale_rappresentante: d.legale_rappresentante || "",
    indirizzo: [d.sede_indirizzo, d.sede_cap, d.sede_citta, d.sede_provincia].filter(Boolean).join(" "),
    cap: d.sede_cap || "",
    citta: d.sede_citta || "",
    provincia: d.sede_provincia || "",
  };
}
